'use client'

import * as React from 'react'
import Link, { LinkProps } from 'next/link'
import { useRouter } from 'next/navigation'

// Components
import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
  DrawerTitle,
} from '@/components/ui/drawer'

// Utils
import { cn } from '@/lib/utils'
import { useMetaColor } from '@/hooks/use-meta-color'

// Config

export function MobileNav() {
  const [open, setOpen] = React.useState(false)
  const { setMetaColor, metaColor } = useMetaColor()

  const onOpenChange = React.useCallback(
    (open: boolean) => {
      setOpen(open)
      setMetaColor(open ? '#09090b' : metaColor)
    },
    [setMetaColor, metaColor]
  )

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerTrigger asChild>
        <Button
          variant="ghost"
          className="-ml-2 mr-2 size-8 px-0 text-base text-background  focus-visible:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 md:hidden"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="!size-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.75 9h16.5m-16.5 6.75h16.5"
            />
          </svg>
          <span className="sr-only">Toggle Menu</span>
        </Button>
      </DrawerTrigger>
      <DrawerContent className="max-h-[60svh] p-0">
        <DrawerTitle className="sr-only">Mobile navbar</DrawerTitle>
        <div className="overflow-auto p-6">
          <div className="flex flex-col space-y-3">
            <MobileLink href="/" onOpenChange={setOpen}>
              Home
            </MobileLink>
            <MobileLink href="/pricing" onOpenChange={setOpen}>
              Pricing
            </MobileLink>
            <MobileLink href="/blog" onOpenChange={setOpen}>
              Blog
            </MobileLink>
            <MobileLink href="/contact" onOpenChange={setOpen}>
              Contact
            </MobileLink>
            <MobileLink href="/#faq" onOpenChange={setOpen}>
              FAQ
            </MobileLink>
            <div className="flex flex-col space-y-2">
              <div className="flex flex-col space-y-3 pt-6">
                <h4 className="font-medium">Services</h4>

                <MobileLink
                  href="/twitch"
                  onOpenChange={setOpen}
                  className="text-muted-foreground"
                >
                  Twitch
                </MobileLink>
                <MobileLink
                  href="/kick"
                  onOpenChange={setOpen}
                  className="text-muted-foreground"
                >
                  Kick
                </MobileLink>
              </div>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}

interface MobileLinkProps extends LinkProps {
  onOpenChange?: (open: boolean) => void
  children: React.ReactNode
  className?: string
}

function MobileLink({
  href,
  onOpenChange,
  className,
  children,
  ...props
}: MobileLinkProps) {
  const router = useRouter()
  return (
    <Link
      href={href}
      onClick={() => {
        router.push(href.toString())
        onOpenChange?.(false)
      }}
      className={cn('text-base', className)}
      {...props}
    >
      {children}
    </Link>
  )
}
